import { Add, Delete, Edit } from "@mui/icons-material";
import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";
import {
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TableCell,
  Tooltip,
  Typography,
} from "@mui/material";
import EmptyContent from "components/EmptyContent";
import List from "components/UI/List";
import { useAlert } from "contexts/alertContext";
import { Feature } from "models/api";
import React, { useCallback, useEffect, useState } from "react";
import { QueryClient, useMutation } from "react-query";
import LockPersonIcon from "@mui/icons-material/LockPerson";
import Search from "components/Search/Search";
import { handleError } from "helpers/utils";
import {
  RolesCells,
  addRole,
  deleteRole,
  editRole,
  getRoles,
} from "framework/roles";
import { IRole, IRoleInput } from "models/Roles";
import AddRole from "./partials/AddRole/AddRoles";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import EditRole from "./partials/EditRole/EditRole";
import DeleteRole from "./partials/DeleteRole/DeleteRole";
import { getPermission, getUserData } from "helpers";
import { IPermission } from "models/permissions";
import BlockIcon from "@mui/icons-material/Block";

interface Props {
  feature: Feature;
}

const Roles: React.FC<Props> = ({ feature }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [loading3, setLoading3] = useState<boolean>(true);
  const [permission, setPermission] = useState<IPermission | null>(null);
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [loading2, setLoading2] = useState<boolean>(true);
  const { t, i18n } = useTranslation();
  const [Roles, setRoles] = useState<IRole[]>([] as IRole[]);
  const [FilterRoles, setFilterRoles] = useState<IRole[]>([] as IRole[]);
  const [selectedRole, setSelectedRole] = useState<IRole>({} as IRole);
  const [inputObj, setInputObj] = useState<IRoleInput>({} as IRoleInput);
  const { setAlert } = useAlert();
  const user = getUserData();
  const { colors } = user;
  // eslint-disable-next-line
  const queryClient = new QueryClient();

  const addRoleMutation = useMutation({
    mutationFn: (createInput: IRoleInput) => {
      return addRole(createInput);
    },
  });

  const editRoleMutation = useMutation({
    mutationFn: (data: { createInput: IRoleInput; id: string }) => {
      return editRole(data.createInput, data.id);
    },
  });

  const deleteRoleMutation = useMutation({
    mutationFn: (id: string) => {
      return deleteRole(id);
    },
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let response: any = await queryClient.fetchQuery("get Roles", () =>
        getRoles()
      );
      response = response = response && response.length > 0 ? response : [];
      setRoles(response);
      setFilterRoles(response);
      setLoading(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  const fetchPermission = useCallback(async () => {
    setLoading3(true);
    try {
      const res = await getPermission(27);
      setPermission(res);
      setLoading3(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading3(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  useEffect(() => {
    fetchData();
    fetchPermission();
    // eslint-disable-next-line
  }, []);
  if (loading || loading3)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  if (!permission?.hasReadPermission)
    return (
      <EmptyContent
        icon={<BlockIcon sx={{ fontSize: "3.5rem " }} />}
        header={t("T_no_permissions")}
        message={t("T_no_permissions_message")}
      />
    );
  const handleOpenAdd = () => setOpenAdd(true);
  const handleCloseAdd = () => setOpenAdd(false);
  const handleOpenEdit = (City: IRole) => {
    setSelectedRole(City);
    setOpenEdit(true);
  };
  const handleCloseEdit = () => {
    setOpenEdit(false);
    setSelectedRole({} as IRole);
  };

  const handleOpenDelete = (City: IRole) => {
    setSelectedRole(City);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setSelectedRole({} as IRole);
  };

  const handleChange = (name: string, value: any) => {
    setInputObj((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAdd = async () => {
    if (!inputObj.name)
      return setAlert({
        open: true,
        message: "Please fill all fields",
        type: "error",
      });
    try {
      setLoading2(true);

      await addRoleMutation.mutateAsync(inputObj);
      setAlert({
        open: true,
        message: "Role added successfully",
        type: "success",
      });
      fetchData();
      handleCloseAdd();
      setInputObj({} as IRoleInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleEdit = async () => {
    try {
      setLoading2(true);
      let createInput: IRoleInput = {
        name: inputObj.name || selectedRole.name,
      };
      await editRoleMutation.mutateAsync({
        createInput,
        id: selectedRole.id,
      });
      setAlert({
        open: true,
        message: "Role updated successfully",
        type: "success",
      });
      fetchData();
      handleCloseEdit();
      setInputObj({} as IRoleInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading2(true);
      const res = await deleteRoleMutation.mutateAsync(selectedRole.id);
      if (!res?.success) throw new Error(res.message);
      setAlert({
        open: true,
        message: "Role deleted successfully",
        type: "success",
      });
      fetchData();
      handleCloseDelete();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const getBodyCells = (row: IRole) => {
    let cells = Object.keys(row).map((key: string, i: number) => {
      if (key === "name")
        return {
          id: "name",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.name}</Typography>
            </TableCell>
          ),
        };

      if (key === "id")
        return {
          id: "actions",
          element: (
            <TableCell align={"left"} key={i}>
              <Stack
                direction={"row"}
                alignContent={"center"}
                spacing={1}
                justifyContent={i18n.language === "en" ? "start" : "end"}
              >
                <Tooltip title="Permissions">
                  <Link to={`/roles/${row?.id}`}>
                    <IconButton size={"small"}>
                      <LockPersonIcon />
                    </IconButton>
                  </Link>
                </Tooltip>

                <Tooltip title={t("T_edit")}>
                  <IconButton
                    size={"small"}
                    color={"primary"}
                    onClick={() => handleOpenEdit(row)}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>

                <Tooltip title={t("T_delete")}>
                  <IconButton
                    size={"small"}
                    sx={{
                      color: colors.deleteIconColor
                        ? colors.deleteIconColor
                        : "red",
                      "&:hover": { color: colors?.deleteIconHoverColor },
                    }}
                    onClick={() => handleOpenDelete(row)}
                  >
                    <Delete />
                  </IconButton>
                </Tooltip>
              </Stack>
            </TableCell>
          ),
        };
    });
    return cells;
  };

  const handleSearch = async (value: string) => {
    if (!value) return setFilterRoles(Roles);
    let filtered = Roles.filter((role) =>
      role.name.toLowerCase().includes(value.toLowerCase())
    );
    setFilterRoles(filtered);
  };

  return (
    <>
      <Stack spacing={2} p={2}>
        <Grid
          container
          justifyContent={"space-between"}
          alignItems="center"
          sx={{ mb: 4 }}
        >
          <Typography
            variant="h3"
            color={colors?.labelColor ? colors?.labelColor : "header.primary"}
          >
            {t(feature.name)}
          </Typography>

          <Stack direction={"row"} spacing={1}>
            <Search handleChange={handleSearch} />
            {/* <Tooltip title={t("T_add_role")}>
              <IconButton
                sx={{
                  color: colors.addUpdateIconColor
                    ? colors.addUpdateIconColor
                    : "red",
                  "&:hover": { color: colors?.addUpdateIconHoverColor },
                  height: 40,
                  width: 40,
                  transition: "0.5s",
                }}
                onClick={handleOpenAdd}
              >
                <Add />
              </IconButton>
            </Tooltip> */}
          </Stack>
        </Grid>

        {FilterRoles.length > 0 && (
          <List
            headCells={RolesCells}
            data={FilterRoles}
            getBodyCells={getBodyCells}
            feature={feature}
            canEdit={false}
          />
        )}
        {(!FilterRoles || FilterRoles.length === 0) && (
          <EmptyContent
            icon={<BlindsClosedIcon sx={{ fontSize: "3.5rem " }} />}
            header={t("T_no_role_header")}
            message={t("T_no_role_message")}
          />
        )}

        {openAdd && (
          <AddRole
            open={openAdd}
            loading={loading2}
            onClose={handleCloseAdd}
            onSubmit={handleAdd}
            onChanges={handleChange}
          />
        )}

        {openEdit && (
          <EditRole
            open={openEdit}
            loading={loading2}
            role={selectedRole}
            onClose={handleCloseEdit}
            onSubmit={handleEdit}
            onChanges={handleChange}
          />
        )}

        {openDelete && (
          <DeleteRole
            open={openDelete}
            loading={loading2}
            onClose={handleCloseDelete}
            onSubmit={handleDelete}
          />
        )}
      </Stack>
    </>
  );
};

export default Roles;
